/**
 * "Run training": staff pick what to train on, confirm, and the api creates
 * the launcher Job. Two sources: a set for apex's SFT recipes, keeper
 * (TRA-1184) or uploaded (TRA-1343), or a GRPO registry dataset trained for
 * a chosen number of steps (TRA-1377). A demo team picks how its simulated
 * run ends instead. The button itself lives on TrainingPage; this is the
 * dialog behind it.
 */

import { useState } from 'react'

import { toast } from 'sonner'

import type {
  GrpoRecipeValues,
  TrainingGrpoOptionsResponse,
  TrainingLaunchRequest,
  TrainingMultiNodeOption,
  TrainingSetOption,
} from '@/api/generated/model'
import { getApiErrorDetail } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useLaunchTrainingRun, useTrainingGrpoPresetValues } from '@/hooks/useTrainingRuns'

import {
  defaultSource,
  GRPO_CHANGE_FIELDS,
  GRPO_CHANGE_FLAGS,
  GRPO_STEPS,
  grpoChanges,
  grpoDatasetSummary,
  grpoSteps,
  lengthGpus,
  multiNodeDescription,
  recipePlaceholder,
  sequenceHelp,
  sequenceSummary,
  setSummary,
  SFT_UNTOUCHED,
  sftAdvanced,
  sftAdvancedValid,
  sftLaunchRequest,
  sftPlan,
} from './launch'
import type { GrpoChangeTexts, GrpoContinuation, LaunchSource, SftAdvancedOptions, SftAdvancedTyped } from './launch'
import { PresetField } from './PresetField'
import { SftAdvancedSection, SftCheckpointField } from './SftStartFields'
import { ValidationDatasetsField } from './ValidationDatasetsField'

type DemoScenario = NonNullable<TrainingLaunchRequest['demo_scenario']>

/** Demo teams only: how the simulated run ends. Order is the order shown. */
const DEMO_SCENARIOS: { id: DemoScenario; label: string }[] = [
  { id: 'completed', label: 'Completes and deploys' },
  { id: 'no-capacity', label: 'Fails: no spot capacity' },
  { id: 'setup-failed', label: 'Fails: node setup' },
  { id: 'training-failed', label: 'Fails: training step' },
  { id: 'deploy-failed', label: 'Fails: Baseten deployment' },
  { id: 'stopped', label: 'Stopped: launcher died' },
]

const SOURCES: { id: LaunchSource; label: string }[] = [
  { id: 'set', label: 'Data' },
  { id: 'grpo', label: 'GRPO dataset' },
]

const DEMO_DESCRIPTION =
  'This is a demo team, so no node starts. The run plays a scripted timeline through the same status updates a real run reports, and ends the way you pick.'
/** A keeper set's launch; `plan` (sftPlan) says what the Advanced section chose. */
function setDescription(plan: string): string {
  return `This starts one 8xH200 or 8xH100 node, spot when available and on-demand otherwise. It fine-tunes the base model with the SFT recipe on the slices of the data you picked${plan}. The run uploads the LoRA adapter, and nothing deploys it yet. There is no held-out eval. The node shuts down when the run ends.`
}
const GRPO_DESCRIPTION =
  "This starts one 8xH200 or 8xH100 node, spot when available and on-demand otherwise. It trains the preset with GRPO on the dataset's training tasks for up to the chosen number of steps and validates on the preset's schedule. The run uploads each saved step as an adapter a later run can start from, and keeps its newest full checkpoint so a later run can continue it. Nothing deploys them yet. The node shuts down when the run ends."
const CONTINUE_DESCRIPTION =
  "This starts one 8xH200 or 8xH100 node, spot when available and on-demand otherwise. It restores the run's saved checkpoint with its optimizer and step count, and trains on from that step up to the total number of steps below. The new run has its own page and uploads its own adapters and checkpoint. The node shuts down when the run ends."
/** An uploaded set's launch; `plan` (sftPlan) says what the Advanced section chose. */
function uploadDescription(plan: string): string {
  return `This starts one 8xH200 or 8xH100 node, spot when available and on-demand otherwise. It fine-tunes the base model on the training file of the SFT set you picked${plan}, and the page shows loss on its validation file. The run uploads the LoRA adapter, and nothing deploys it yet. The node shuts down when the run ends.`
}

function formatGeneratedAt(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function LaunchRunDialog({
  open,
  onOpenChange,
  sets,
  multiNode,
  advanced,
  grpo,
  demo,
  continuation = null,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  sets: TrainingSetOption[]
  /** What the Advanced section offers a set launch (TRA-1345); undefined while the sets have not answered. */
  advanced: SftAdvancedOptions | undefined
  /** How a set launch spans nodes where the deployment turns multi-node training on (TRA-1560); null elsewhere. */
  multiNode: TrainingMultiNodeOption | null
  grpo: TrainingGrpoOptionsResponse
  /** A restricted demo team: the run is simulated and the scenario picker shows. */
  demo: boolean
  /** Opened from a run's Continue button: its fields start as that run's (TRA-1430). */
  continuation?: GrpoContinuation | null
}): React.JSX.Element {
  const [source, setSource] = useState<LaunchSource>(
    continuation === null ? defaultSource({ sets, grpo: grpo.datasets }) : 'grpo',
  )
  const [setId, setSetId] = useState<string>(sets[0]?.set_id ?? '')
  const [datasetId, setDatasetId] = useState<string>(
    continuation?.datasetId ?? grpo.datasets[0]?.registry_id ?? '',
  )
  // Every validation dataset is ticked to start, as the AWS runs validate.
  const [validationIds, setValidationIds] = useState<string[]>(
    continuation?.validationIds ?? grpo.validation_datasets.map((option) => option.registry_id),
  )
  const [checkpoint, setCheckpoint] = useState<string>(continuation?.checkpoint ?? '')
  const [sftCheckpoint, setSftCheckpoint] = useState<string>('')
  // What was typed or picked in the Advanced fields; null shows the picked
  // set's recipe default, so a reset or another set's recipe takes over.
  const [sftTyped, setSftTyped] = useState<SftAdvancedTyped>(SFT_UNTOUCHED)
  const [preset, setPreset] = useState<string>(continuation?.preset ?? grpo.default_preset)
  const [stepsText, setStepsText] = useState<string>(
    continuation?.stepsText ?? String(GRPO_STEPS.default),
  )
  const [changeTexts, setChangeTexts] = useState<GrpoChangeTexts>(continuation?.changeTexts ?? {})
  // The chosen preset's values, which the recipe fields show (each read goes to the trainer).
  const presetValues = useTrainingGrpoPresetValues(preset, source === 'grpo' && !demo)
  const currentValues = presetValues.data
  const presetError = presetValues.isError
    ? (getApiErrorDetail(presetValues.error) ?? 'The preset could not be read.')
    : undefined
  const [scenario, setScenario] = useState<DemoScenario>('completed')
  const launch = useLaunchTrainingRun()

  const picked = sets.find((option) => option.set_id === setId)
  // How the picked set's rows fit a multi-node run's ranks (TRA-1564); null where a run trains on one node.
  const fit = picked?.multi_node ?? null
  const setReady = picked !== undefined && (fit === null || fit.launches)
  const steps = grpoSteps(stepsText)
  const recipe = grpoChanges(changeTexts)
  const grpoReady =
    grpo.datasets.some((option) => option.registry_id === datasetId) &&
    preset.trim() !== '' &&
    steps !== null &&
    recipe.invalid.length === 0
  // A demo run trains nothing, so it names no source.
  const useSet = source === 'set' && !demo
  const useGrpo = source === 'grpo' && !demo
  // The Advanced fields for the picked set: its recipe's defaults, or what
  // was typed, each checked against the api's bounds (TRA-1345).
  const sftFields =
    advanced !== undefined && picked !== undefined
      ? sftAdvanced(advanced, picked.recipe, sftTyped, fit)
      : null
  // The length a multi-node run trains at, and the GPUs that share each
  // sequence there (TRA-1559): the set's own, or the longer one picked.
  const length = sftFields?.automodel?.length ?? null
  const sftValid = sftFields !== null && sftAdvancedValid(sftFields)
  const plan = sftFields === null ? '' : sftPlan(sftFields)
  const ready = demo || (useGrpo ? grpoReady : setReady && sftValid)
  const pending = launch.isPending

  async function handleLaunch(): Promise<void> {
    if (useGrpo && steps !== null) {
      await launch.mutateAsync({
        data: {
          grpo_dataset: datasetId,
          preset: preset.trim(),
          max_steps: steps,
          ...(checkpoint.trim() === '' ? {} : { initial_checkpoint: checkpoint.trim() }),
          ...(validationIds.length === 0 ? {} : { grpo_validation_datasets: validationIds }),
          ...(recipe.changes === null ? {} : { grpo_changes: recipe.changes }),
        },
      })
      onOpenChange(false)
      toast.success(
        continuation === null
          ? `The GRPO run on ${datasetId} started.`
          : `The GRPO run on ${datasetId} continues from step ${String(continuation.step)}.`,
      )
      return
    }
    if (useSet) {
      if (sftFields === null) return
      const startingAdapter = picked?.recipe === 'sft-chat' && multiNode === null ? sftCheckpoint : ''
      const request = sftLaunchRequest(setId, startingAdapter, sftFields)
      if (request === null) return
      await launch.mutateAsync({ data: request })
      onOpenChange(false)
      toast.success(`The training run on set ${setId} started.`)
      return
    }
    await launch.mutateAsync({ data: { demo_scenario: scenario } })
    onOpenChange(false)
    toast.success('The demo run started.')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{continuation === null ? 'Run training' : 'Continue run'}</DialogTitle>
          <DialogDescription>
            {demo
              ? DEMO_DESCRIPTION
              : useGrpo
                ? continuation === null
                  ? GRPO_DESCRIPTION
                  : CONTINUE_DESCRIPTION
                : multiNode !== null
                  ? multiNodeDescription(multiNode, plan)
                  : picked?.kind === 'upload'
                    ? uploadDescription(plan)
                    : setDescription(plan)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* A demo run trains nothing; a continued run trains on its own
              run's source. */}
          {!demo && continuation === null && (
            <div className="space-y-2">
              <Label htmlFor="launch-source">Train on</Label>
              <Select
                value={source}
                onValueChange={(next) => {
                  setSource(next === 'grpo' ? 'grpo' : 'set')
                }}
              >
                <SelectTrigger id="launch-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCES.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {useSet && (
            <div className="space-y-2">
              <Label htmlFor="launch-set">Data</Label>
              <Select value={setId} onValueChange={setSetId} disabled={sets.length === 0}>
                <SelectTrigger id="launch-set">
                  <SelectValue placeholder="Pick data" />
                </SelectTrigger>
                <SelectContent>
                  {sets.map((option) => (
                    <SelectItem key={option.set_id} value={option.set_id}>
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono text-xs">{option.set_id}</span>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                          {setSummary(option, formatGeneratedAt(option.generated_at))}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {sets.length === 0
                  ? 'No data is published yet. Upload some, or publish keeper data with `training_keepers set`.'
                  : "The team's keeper data and uploaded data appear here, newest first."}
              </p>
              {fit?.notice !== undefined && fit.notice !== null && (
                <p
                  role={fit.launches ? undefined : 'alert'}
                  className={fit.launches ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}
                >
                  {fit.notice}
                </p>
              )}
            </div>
          )}
          {useSet && picked?.recipe === 'sft-chat' && multiNode === null && (
            <SftCheckpointField value={sftCheckpoint} onChange={setSftCheckpoint} pending={pending} />
          )}
          {useSet && multiNode !== null && (
            <div className="space-y-2">
              <Label htmlFor="launch-nodes">Nodes</Label>
              <p id="launch-nodes" className="font-mono text-sm">
                {multiNode.node_count} nodes of {multiNode.gpus_per_node} GPUs
              </p>
              <p className="text-xs text-muted-foreground">
                The base model {multiNode.base_model} trains on this many nodes at once.
              </p>
            </div>
          )}
          {useSet && multiNode !== null && length !== null && (
            <div className="space-y-2">
              <Label htmlFor="launch-sequence">Sequence length</Label>
              <p id="launch-sequence" className="font-mono text-sm">
                {sequenceSummary(length.value, lengthGpus(length))}
              </p>
              <p className="text-xs text-muted-foreground">{sequenceHelp(picked?.longest_row_tokens)}</p>
            </div>
          )}
          {useSet && sftFields !== null && (
            <SftAdvancedSection
              advanced={sftFields}
              pending={pending}
              onChange={(changes) => {
                setSftTyped((typed) => ({ ...typed, ...changes }))
              }}
              onReset={() => {
                setSftTyped(SFT_UNTOUCHED)
              }}
            />
          )}
          {useGrpo && (
            <GrpoFields
              grpo={grpo}
              datasetId={datasetId}
              onDatasetChange={setDatasetId}
              validationIds={validationIds}
              onValidationChange={setValidationIds}
              checkpoint={checkpoint}
              onCheckpointChange={setCheckpoint}
              preset={preset}
              onPresetChange={setPreset}
              presetError={presetError}
              stepsText={stepsText}
              onStepsChange={setStepsText}
            />
          )}
          {useGrpo && (
            <RecipeChangesField
              texts={changeTexts}
              invalid={recipe.invalid}
              current={currentValues}
              onChange={setChangeTexts}
            />
          )}
          {demo && (
            <div className="space-y-2">
              <Label htmlFor="launch-scenario">Outcome</Label>
              <Select
                value={scenario}
                onValueChange={(next) => {
                  setScenario(next as DemoScenario)
                }}
              >
                <SelectTrigger id="launch-scenario">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEMO_SCENARIOS.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleLaunch()
            }}
            disabled={pending || !ready}
          >
            {pending ? 'Launching...' : continuation === null ? 'Run training' : 'Continue run'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The grpo source's fields: the dataset, its validation datasets, the checkpoint it starts from, the preset and the steps. */
function GrpoFields({
  grpo,
  datasetId,
  onDatasetChange,
  validationIds,
  onValidationChange,
  checkpoint,
  onCheckpointChange,
  preset,
  onPresetChange,
  presetError,
  stepsText,
  onStepsChange,
}: {
  grpo: TrainingGrpoOptionsResponse
  datasetId: string
  onDatasetChange: (id: string) => void
  validationIds: string[]
  onValidationChange: (ids: string[]) => void
  checkpoint: string
  onCheckpointChange: (path: string) => void
  preset: string
  onPresetChange: (name: string) => void
  /** Why the preset's values could not be read (an unknown or unsupported preset); undefined when they were. */
  presetError: string | undefined
  stepsText: string
  onStepsChange: (text: string) => void
}): React.JSX.Element {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="launch-grpo-dataset">Dataset</Label>
        <Select value={datasetId} onValueChange={onDatasetChange} disabled={grpo.datasets.length === 0}>
          <SelectTrigger id="launch-grpo-dataset">
            <SelectValue placeholder="Pick a dataset" />
          </SelectTrigger>
          <SelectContent>
            {grpo.datasets.map((option) => (
              <SelectItem key={option.registry_id} value={option.registry_id}>
                <span className="flex items-baseline gap-2">
                  <span className="font-mono text-xs">{option.registry_id}</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {grpoDatasetSummary(option)}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {grpo.datasets.length === 0 && (
          <p className="text-xs text-muted-foreground">No GRPO dataset is registered for this team yet.</p>
        )}
      </div>
      {grpo.validation_datasets.length > 0 && (
        <ValidationDatasetsField
          options={grpo.validation_datasets}
          selected={validationIds}
          onChange={onValidationChange}
        />
      )}
      <div className="space-y-2">
        <Label htmlFor="launch-grpo-checkpoint">Starting checkpoint</Label>
        <Input
          id="launch-grpo-checkpoint"
          className="font-mono text-xs"
          placeholder="training-runs/team/run/exports/adapters/step_5/"
          value={checkpoint}
          onChange={(event) => {
            onCheckpointChange(event.target.value)
          }}
        />
        <p className="text-xs text-muted-foreground">
          Leave this empty to train a fresh adapter. A folder in a run&apos;s exports or the
          team&apos;s training-bundles area holds either a sealed adapter (initialization.json), which
          starts a fresh optimizer, or a run&apos;s saved checkpoint (resume.json), which continues
          that run&apos;s optimizer and step count under the same preset and recipe changes.
        </p>
      </div>
      <PresetField
        id="launch-grpo-preset"
        presets={grpo.presets}
        value={preset}
        error={presetError}
        onChange={onPresetChange}
      />
      <div className="space-y-2">
        <Label htmlFor="launch-grpo-steps">Optimizer steps</Label>
        <Input
          id="launch-grpo-steps"
          type="number"
          min={GRPO_STEPS.min}
          max={GRPO_STEPS.max}
          value={stepsText}
          onChange={(event) => {
            onStepsChange(event.target.value)
          }}
        />
        <p className="text-xs text-muted-foreground">
          {`Enter ${String(GRPO_STEPS.min)} to ${String(GRPO_STEPS.max)}. A run ends early when it reaches the preset's epoch limit. A run continued from a saved checkpoint counts from that checkpoint's step, so this is the total.`}
        </p>
      </div>
    </>
  )
}

/** Recipe values the launch may change; an empty field keeps the preset's value. */
function RecipeChangesField({
  texts,
  invalid,
  current,
  onChange,
}: {
  texts: GrpoChangeTexts
  invalid: (keyof GrpoChangeTexts)[]
  /** The preset's current values, shown in the empty fields; undefined until read. */
  current: GrpoRecipeValues | undefined
  onChange: (texts: GrpoChangeTexts) => void
}): React.JSX.Element {
  const changed = Object.values(texts).filter((text) => text.trim() !== '').length
  return (
    <details className="border border-border p-3" open={changed > 0}>
      <summary className="cursor-pointer font-mono text-xs uppercase tracking-wider text-muted-foreground">
        Recipe changes{changed > 0 ? ` (${changed.toString()})` : ''}
      </summary>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {GRPO_CHANGE_FIELDS.map((field) => {
          const id = `launch-grpo-change-${field.key}`
          const bad = invalid.includes(field.key)
          return (
            <div key={field.key} className="space-y-1">
              <Label htmlFor={id} className="text-xs">
                {field.label}
              </Label>
              <Input
                id={id}
                inputMode="decimal"
                className="font-mono text-xs"
                placeholder={recipePlaceholder(current, field.key)}
                value={texts[field.key] ?? ''}
                aria-invalid={bad}
                aria-describedby={bad ? `${id}-error` : undefined}
                onChange={(event) => {
                  onChange({ ...texts, [field.key]: event.target.value })
                }}
              />
              {bad && (
                <p id={`${id}-error`} className="text-xs text-destructive">
                  {`Enter ${field.integer ? 'a whole number' : 'a number'} ${field.aboveMin ? 'above' : 'from'} ${String(field.min)} to ${String(field.max)}.`}
                </p>
              )}
            </div>
          )
        })}
        {GRPO_CHANGE_FLAGS.map((flag) => {
          const id = `launch-grpo-change-${flag.key}`
          return (
            <div key={flag.key} className="space-y-1">
              <Label htmlFor={id} className="text-xs">
                {flag.label}
              </Label>
              <Select
                value={texts[flag.key] ?? ''}
                onValueChange={(value) => {
                  onChange({ ...texts, [flag.key]: value === 'preset' ? '' : value })
                }}
              >
                <SelectTrigger id={id} className="font-mono text-xs">
                  <SelectValue placeholder={recipePlaceholder(current, flag.key)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="preset">Preset value</SelectItem>
                  <SelectItem value="on">On</SelectItem>
                  <SelectItem value="off">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )
        })}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Leave a field empty to keep the preset&apos;s value. A temperature change applies to
        validation too, so validation rewards compare only with runs at the same temperature. The run
        card lists every change.
      </p>
    </details>
  )
}
